import { Runtime } from "foldkit";
import type * as ManagedResource from "foldkit/managedResource";
import * as Subscription from "foldkit/subscription";

import { apiLayer } from "./api/client";
import { a2aContextSubscriptions } from "./app/a2aContext";
import { initAnalytics } from "./app/analytics";
import { billingPreviewSubscriptions } from "./app/billingPreview";
import { withInteractionAnalytics } from "./app/interactionAnalytics";
import {
	Message,
	Model,
	aiSubscriptions,
	balanceSubscriptions,
	chatSubscriptions,
	init,
	makeIdentifyAnalytics,
	makeSignOut,
	makeUpdate,
	makeView,
	searchSubscriptions,
	shortcutSubscriptions,
	timeTravelSubscriptions,
} from "./app/main";
import { installNamedInteractions } from "./app/namedInteractions";
import { makeClerkAdapter } from "./auth/clerk";
import { makeClerkMounts } from "./auth/mounts";
import {
	type ClerkSessionService,
	makeSessionResources,
	sessionSubscriptions,
} from "./auth/sessionResource";
import { makeClerkTokenLayer } from "./auth/tokenService";
import { stashCaptureFragment } from "./capture/payloadService";
import type { Api } from "./pages/shared";

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!publishableKey) {
	throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

// Clerk and analytics are constructed here at boot, inside a function, never at module scope.
const boot = () => {
	// KTD3: captured LinkedIn fields ride in the `/capture` fragment; stash and strip
	// them before analytics or routing ever see the URL.
	stashCaptureFragment(window.location, window.sessionStorage, (url) =>
		window.history.replaceState(null, "", url),
	);
	const analytics = initAnalytics({
		mode: import.meta.env.MODE,
		posthogToken: import.meta.env.VITE_POSTHOG_PROJECT_TOKEN,
		posthogHost: import.meta.env.VITE_POSTHOG_HOST,
		rollbarToken: import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN,
	});
	// Back from Stripe Checkout or the portal restores this page from the
	// back/forward cache exactly as it left, mid-redirect ("Opening billing...").
	// Reload so the page state comes from the server again.
	window.addEventListener("pageshow", (event) => {
		if (event.persisted) window.location.reload();
	});
	const removeNamedInteractions = installNamedInteractions(document);
	import.meta.hot?.dispose(removeNamedInteractions);
	const adapter = makeClerkAdapter(publishableKey);
	const managedResources: ManagedResource.ManagedResources<
		Model,
		Message,
		ClerkSessionService
	> = makeSessionResources(adapter);
	const application = Runtime.makeApplication({
		Model,
		init,
		update: withInteractionAnalytics(
			makeUpdate(makeIdentifyAnalytics(analytics), makeSignOut(adapter)),
		),
		view: makeView(makeClerkMounts(adapter)),
		routing: {
			onUrlRequest: (request) => Message.ClickedLink({ request }),
			onUrlChange: (url) => Message.ChangedUrl({ url }),
		},
		subscriptions: Subscription.aggregate<
			Model,
			Message,
			ClerkSessionService | Api
		>()(
			sessionSubscriptions,
			balanceSubscriptions,
			billingPreviewSubscriptions,
			a2aContextSubscriptions,
			searchSubscriptions,
			shortcutSubscriptions,
			timeTravelSubscriptions,
			chatSubscriptions,
			aiSubscriptions,
		),
		managedResources,
		resources: apiLayer(makeClerkTokenLayer(adapter)),
		container: document.getElementById("root"),
		devTools: import.meta.env.DEV ? { Message } : false,
	});
	Runtime.run(application);
};

boot();
